import { useLazyQuery, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useRemoteRenderContext } from "../../../../classes/contexts/RemoteRenderContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { IconSize } from "../../../../classes/enums/ButtonEnum";
import { DigitalTwinDisplayTypes } from "../../../../classes/enums/DigitalTwinEnum";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import {
  InstanceOrientation,
  MORPHOLOGY_ORIENTATION_ORDER,
  MorphologyOrientation,
  SPINESIM_MODALITIES,
} from "../../../../classes/enums/MedicalImagesEnum";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import { formatTwinCreationDate } from "../../../../classes/utils/DateUtils";
import { convertKey } from "../../../../classes/utils/MedicalImagesUtils";
import { getRemoteMedicalViewState } from "../../../../classes/utils/RemoteMedicalViewStateUtils";
import { generateCacheKey } from "../../../../classes/utils/RemoteRenderingUtils";
import { getGroupedThumbnails } from "../../../../classes/utils/Thumbnailsutils";
import IconButton from "../../../../components/iconButton/IconButton";
import ImageCarousel from "../../../../components/imageCarousel/ImageCarousel";
import ImageViewer from "../../../../components/imageViewer/ImageViewer";
import {
  DEFAULT_TOOL,
  getToolConfig,
} from "../../../../components/painAssessmentToolSelector/painAssesmentConfig";
import RemoteViewer from "../../../../components/remoteViewer/RemoteViewer";
import ThumbnailTab from "../../../../components/thumbnailTab/ThumbnailTab";
import ToggleButton from "../../../../components/toggleButton/ToggleButton";
import ViewCarousel from "../../../../components/viewCarousel/ViewCarousel";
import ViewLoader from "../../../../components/viewLoader/ViewLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { usePatientInstancesByModality } from "../../../../hooks/queries/usePatientInstancesByModality";
import { useAuth } from "../../../../hooks/useAuth";
import useFileStorage from "../../../../hooks/useFileStorage";
import { usePatientMorphologyImages } from "../../../../hooks/usePatientMorphologyImages";
import { useRouteState } from "../../../../hooks/useRouteState";
import { GET_CLINICAL_HISTORY_BY_ID } from "../../../../queries/clinicalHistoryQueries";
import { GET_HEIGHTS_BY_IDS } from "../../../../queries/heightQueries";
import { GET_PATIENT_CONDITION_BY_ID } from "../../../../queries/patientConditionQueries";
import { GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS } from "../../../../queries/qualityOfLifeIndicatorQueries";
import { GET_WEIGHTS_BY_IDS } from "../../../../queries/weightQueries";
import PainAssessmentViewer from "../../../common/painAssesment/PainAssessmentViewer";
import PatientInformation from "../../widgets/patientInformation/PatientInformation";
import SpineMeasurements from "../../widgets/sppInformation/SpineMeasurements";
import DeleteDigitalTwinModal from "../digitalTwinTimeline/DeleteDigitalTwinModal";
import "./digitalTwinVisualization.css";
import PainAssessmentBodyDiagram from "./painAssessment/PainAssessmentBodyDiagram";

const GridIcon = IconsEnum.GRID;
const TwinIcon = IconsEnum.BODYLAYERS;
const CompareIcon = IconsEnum.COMPAREDIGITALTWIN;
const EditIcon = IconsEnum.EDIT;
const DeleteIcon = IconsEnum.DELETE;

// Normalize view name to display settings key
const getDisplaySettingsViewType = viewName => {
  if (!viewName) return null;
  const lower = viewName.toLowerCase();
  if (lower.includes("lateral") || lower.includes("sagittal")) return "lateral";
  if (lower.includes("front") || lower.includes("coronal")) return "frontal";
  if (lower.includes("axial")) return "axial";
  return null;
};

const prepareFilteredViews = (views, closedViews, modality) => {
  if (views.length === 0) return [];

  // Filter out closed views
  const filteredViews = views.filter(
    view =>
      !closedViews.has(view.viewData?.viewId || view.instance?._id || view._id)
  );

  // Sort views by name (for CT and MR)
  if ([SPINESIM_MODALITIES.CT, SPINESIM_MODALITIES.MR].includes(modality)) {
    filteredViews.sort((a, b) => {
      if (a.viewName && b.viewName) {
        if (a.viewName.includes("Coronal")) return -1;
        if (b.viewName.includes("Coronal")) return 1;
        if (a.viewName.includes("Axial")) return -1;
        if (b.viewName.includes("Axial")) return 1;
      }
      return a.index - b.index;
    });
  }

  return filteredViews;
};

const prepareCarouselViews = (views, modality) => {
  if (views.length === 0) return [];

  // For CT/MR modalities, exclude axial views for the carousel
  if ([SPINESIM_MODALITIES.CT, SPINESIM_MODALITIES.MR].includes(modality)) {
    return views.filter(
      view => view.viewName && !view.viewName.toLowerCase().includes("axial")
    );
  }

  return views;
};

const getViewsForModality = (
  modality,
  selectedTwin,
  instancesByModality,
  digitalTwinViewCache,
  generateCacheKey,
  visibleInstances
) => {
  if (!selectedTwin | !modality) return [];

  const { DX = [], CT = [], MR = [] } = instancesByModality;
  const views = [];

  // Helper function to process an instance and add its views
  const processInstance = instance => {
    if (!instance) return;

    const cacheKey = generateCacheKey(instance);
    if (!digitalTwinViewCache.hasView(cacheKey)) return;

    const cachedData = digitalTwinViewCache.getView(cacheKey);

    if (Array.isArray(cachedData)) {
      cachedData.forEach((view, index) => {
        views.push({
          key: `${cacheKey}_${
            view.viewName ||
            (index === 0
              ? InstanceOrientation.Sagittal
              : InstanceOrientation.Coronal)
          }`,
          viewData: view,
          instance,
          viewName: view.viewName,
          index,
        });
      });
    } else {
      views.push({
        key: cacheKey,
        viewData: cachedData,
        instance,
        viewName: cachedData.viewName,
        index: 0,
      });
    }
  };

  switch (modality) {
    case SPINESIM_MODALITIES.DX: {
      const { sagittal_dx_instance_id, coronal_dx_instance_id } = selectedTwin;

      // Process sagittal view
      if (sagittal_dx_instance_id) {
        const sagittalInst = DX.find(i => i._id === sagittal_dx_instance_id);
        processInstance(sagittalInst);
      }

      // Process coronal view
      if (coronal_dx_instance_id) {
        const coronalInst = DX.find(i => i._id === coronal_dx_instance_id);
        processInstance(coronalInst);
      }
      break;
    }

    case SPINESIM_MODALITIES.CT: {
      if (!selectedTwin.ct_instance_id) break;

      const ctInst = CT.find(i => i._id === selectedTwin.ct_instance_id);
      processInstance(ctInst);
      break;
    }

    case SPINESIM_MODALITIES.MR: {
      if (!selectedTwin.mri_instance_id) break;

      const mrInst = MR.find(i => i._id === selectedTwin.mri_instance_id);
      processInstance(mrInst);
      break;
    }

    case SPINESIM_MODALITIES.POSTURE:
      // For posture, return the instances directly
      return visibleInstances;

    default:
      break;
  }

  return views;
};

const isViewClosing = (viewId, instanceId, index, viewType, closingViews) => {
  const viewKey = viewId || instanceId || `index-${index}-${viewType}`;
  return closingViews.has(viewKey);
};

// Helper function to get preferred modality - prioritizes DX, then first available
const getPreferredModality = thumbnails => {
  if (!thumbnails || thumbnails.length === 0) return null;

  // Try to find DX modality first
  const dxModality = thumbnails.find(
    t => t.modality === SPINESIM_MODALITIES.DX
  );
  if (dxModality) {
    return dxModality.modality;
  }

  // Fall back to first available modality
  return thumbnails[0].modality;
};

const parseClinicalMetricRef = refValue => {
  if (refValue === null || refValue === undefined) {
    return { id: null, value: null };
  }

  if (typeof refValue === "number") {
    return { id: null, value: refValue };
  }

  const asString = String(refValue).trim();
  if (!asString) {
    return { id: null, value: null };
  }

  if (/^[0-9a-f]{24}$/i.test(asString)) {
    return { id: asString, value: null };
  }

  const parsed = Number(asString);
  return Number.isFinite(parsed)
    ? { id: null, value: parsed }
    : { id: null, value: null };
};

// endregion

const DigitalTwinVisualization = ({
  selectedTwin,
  comparedTwin,
  comparisonMode,
  comparisonModeEnabled,
  renderClient,
  onToggleComparisonMode,
  onDeleteTwin,
}) => {
  const { dateSettings } = useUser();
  const { showPopup } = useAppContext();
  const { t } = useTranslation();
  const { checkPermissions } = useAuth();
  const canDelete = checkPermissions(DataPermissions.DELETE_DIGITAL_TWIN);
  const { instancesByModality, updateLocalDisplaySettings } =
    usePatientInstancesByModality();
  const {
    imagesWithUrls: posturalImages,
    imagesWithUrlsNoBg: posturalImagesNoBg,
  } = usePatientMorphologyImages();

  const { assessmentsByDate: allPainAssessments } = usePatientContext();

  // Fetch clinical history for selected twin
  const { data: clinicalHistoryData } = useQuery(GET_CLINICAL_HISTORY_BY_ID, {
    variables: { id: selectedTwin?.clinical_history_id },
    skip: !selectedTwin?.clinical_history_id,
    fetchPolicy: "cache-first",
  });

  const clinicalHistory = useMemo(
    () => clinicalHistoryData?.clinicalHistoryById || null,
    [clinicalHistoryData]
  );

  // Fetch clinical history for compared twin
  const { data: comparedClinicalHistoryData } = useQuery(
    GET_CLINICAL_HISTORY_BY_ID,
    {
      variables: { id: comparedTwin?.clinical_history_id },
      skip: !comparedTwin?.clinical_history_id,
      fetchPolicy: "cache-first",
    }
  );

  const comparedClinicalHistory = useMemo(
    () => comparedClinicalHistoryData?.clinicalHistoryById || null,
    [comparedClinicalHistoryData]
  );

  const selectedWeightRef = useMemo(
    () => parseClinicalMetricRef(clinicalHistory?.weight),
    [clinicalHistory?.weight]
  );
  const selectedHeightRef = useMemo(
    () => parseClinicalMetricRef(clinicalHistory?.height),
    [clinicalHistory?.height]
  );
  const comparedWeightRef = useMemo(
    () => parseClinicalMetricRef(comparedClinicalHistory?.weight),
    [comparedClinicalHistory?.weight]
  );
  const comparedHeightRef = useMemo(
    () => parseClinicalMetricRef(comparedClinicalHistory?.height),
    [comparedClinicalHistory?.height]
  );

  const { data: selectedWeightData } = useQuery(GET_WEIGHTS_BY_IDS, {
    variables: {
      ids: selectedWeightRef.id ? [selectedWeightRef.id] : [],
    },
    skip: !selectedWeightRef.id,
    fetchPolicy: "cache-first",
  });

  const { data: selectedHeightData } = useQuery(GET_HEIGHTS_BY_IDS, {
    variables: {
      ids: selectedHeightRef.id ? [selectedHeightRef.id] : [],
    },
    skip: !selectedHeightRef.id,
    fetchPolicy: "cache-first",
  });

  const { data: comparedWeightData } = useQuery(GET_WEIGHTS_BY_IDS, {
    variables: {
      ids: comparedWeightRef.id ? [comparedWeightRef.id] : [],
    },
    skip: !comparedWeightRef.id,
    fetchPolicy: "cache-first",
  });

  const { data: comparedHeightData } = useQuery(GET_HEIGHTS_BY_IDS, {
    variables: {
      ids: comparedHeightRef.id ? [comparedHeightRef.id] : [],
    },
    skip: !comparedHeightRef.id,
    fetchPolicy: "cache-first",
  });

  const selectedWeightValue = useMemo(() => {
    if (Number.isFinite(selectedWeightRef.value)) {
      return selectedWeightRef.value;
    }
    const value = selectedWeightData?.weightsByIds?.[0]?.value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [selectedWeightRef.value, selectedWeightData]);

  const selectedHeightValue = useMemo(() => {
    if (Number.isFinite(selectedHeightRef.value)) {
      return selectedHeightRef.value;
    }
    const value = selectedHeightData?.heightsByIds?.[0]?.value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [selectedHeightRef.value, selectedHeightData]);

  const comparedWeightValue = useMemo(() => {
    if (Number.isFinite(comparedWeightRef.value)) {
      return comparedWeightRef.value;
    }
    const value = comparedWeightData?.weightsByIds?.[0]?.value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [comparedWeightRef.value, comparedWeightData]);

  const comparedHeightValue = useMemo(() => {
    if (Number.isFinite(comparedHeightRef.value)) {
      return comparedHeightRef.value;
    }
    const value = comparedHeightData?.heightsByIds?.[0]?.value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [comparedHeightRef.value, comparedHeightData]);

  // Fetch patient conditions
  const [getConditions] = useLazyQuery(GET_PATIENT_CONDITION_BY_ID);
  const [conditionsData, setConditionsData] = useState(null);
  const [getComparedConditions] = useLazyQuery(GET_PATIENT_CONDITION_BY_ID);
  const [conditionsDataCompared, setConditionsDataCompared] = useState(null);
  const [getQualityIndicators] = useLazyQuery(
    GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS
  );
  const [qualityOfLifeIndicators, setQualityOfLifeIndicators] = useState([]);
  const [getComparedQualityIndicators] = useLazyQuery(
    GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS
  );
  const [qualityOfLifeIndicatorsCompared, setQualityOfLifeIndicatorsCompared] =
    useState([]);

  useEffect(() => {
    const fetchConditions = async () => {
      if (!clinicalHistory?.conditions) {
        setConditionsData(null);
        return;
      }

      if (clinicalHistory.conditions.match(/^[0-9a-f]{24}$/i)) {
        try {
          const { data } = await getConditions({
            variables: { id: clinicalHistory.conditions },
          });
          setConditionsData(data?.patientConditionById?.conditions || []);
        } catch (error) {
          console.error("Error fetching conditions:", error);
          setConditionsData(null);
        }
      } else {
        setConditionsData(
          clinicalHistory.conditions.split(",").map(c => c.trim())
        );
      }
    };

    fetchConditions();
  }, [clinicalHistory?.conditions, getConditions]);

  useEffect(() => {
    const fetchComparedConditions = async () => {
      if (!comparedClinicalHistory?.conditions) {
        setConditionsDataCompared(null);
        return;
      }

      if (comparedClinicalHistory.conditions.match(/^[0-9a-f]{24}$/i)) {
        try {
          const { data } = await getComparedConditions({
            variables: { id: comparedClinicalHistory.conditions },
          });
          setConditionsDataCompared(
            data?.patientConditionById?.conditions || []
          );
        } catch (error) {
          console.error("Error fetching compared conditions:", error);
          setConditionsDataCompared(null);
        }
      } else {
        setConditionsDataCompared(
          comparedClinicalHistory.conditions.split(",").map(c => c.trim())
        );
      }
    };

    fetchComparedConditions();
  }, [comparedClinicalHistory?.conditions, getComparedConditions]);

  useEffect(() => {
    const fetchQualityOfLife = async () => {
      const ids = [
        ...(clinicalHistory?.quality_of_life_indicators || []),
        ...(clinicalHistory?.custom_quality_of_life_indicators || []),
      ].filter(Boolean);

      if (!ids.length) {
        setQualityOfLifeIndicators([]);
        return;
      }

      try {
        const { data } = await getQualityIndicators({
          variables: { ids },
        });
        const parsed =
          data?.qualityOfLifeIndicatorsByIds?.map(item => ({
            id: item._id,
            name: item.name,
            value: item.value,
            max_value: item.max_value,
            date: item.date || item.createdAt,
          })) || [];
        setQualityOfLifeIndicators(parsed);
      } catch (error) {
        console.error("Error fetching quality of life indicators:", error);
        setQualityOfLifeIndicators([]);
      }
    };

    fetchQualityOfLife();
  }, [
    clinicalHistory?.quality_of_life_indicators,
    clinicalHistory?.custom_quality_of_life_indicators,
    getQualityIndicators,
  ]);

  useEffect(() => {
    const fetchQualityOfLifeCompared = async () => {
      const ids = [
        ...(comparedClinicalHistory?.quality_of_life_indicators || []),
        ...(comparedClinicalHistory?.custom_quality_of_life_indicators || []),
      ].filter(Boolean);

      if (!ids.length) {
        setQualityOfLifeIndicatorsCompared([]);
        return;
      }

      try {
        const { data } = await getComparedQualityIndicators({
          variables: { ids },
        });
        const parsed =
          data?.qualityOfLifeIndicatorsByIds?.map(item => ({
            id: item._id,
            name: item.name,
            value: item.value,
            max_value: item.max_value,
            date: item.date || item.createdAt,
          })) || [];
        setQualityOfLifeIndicatorsCompared(parsed);
      } catch (error) {
        console.error(
          "Error fetching quality of life indicators for compared twin:",
          error
        );
        setQualityOfLifeIndicatorsCompared([]);
      }
    };

    fetchQualityOfLifeCompared();
  }, [
    comparedClinicalHistory?.quality_of_life_indicators,
    comparedClinicalHistory?.custom_quality_of_life_indicators,
    getComparedQualityIndicators,
  ]);

  const resolveStrokeWidth = useCallback(pathObj => {
    const parsed = Number(pathObj?.strokeWidth);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    const toolConfig = getToolConfig(pathObj?.toolType || DEFAULT_TOOL);
    return toolConfig.strokeWidth || 3;
  }, []);

  const normalizedPainAssessments = useMemo(
    () =>
      (allPainAssessments || []).map(assessment => ({
        ...assessment,
        allPaths: (assessment?.allPaths || []).map(path => ({
          ...path,
          strokeWidth: resolveStrokeWidth(path),
        })),
      })),
    [allPainAssessments, resolveStrokeWidth]
  );

  const [selectedMainTwinModality, setSelectedMainTwinModality] =
    useState(null);
  const [selectedComparedTwinModality, setSelectedComparedTwinModality] =
    useState(null);

  const [, setCurrentModalityInstances] = useState([]);

  const [visibleInstances, setVisibleInstances] = useState([]);
  const [closedDxViews, setClosedDxViews] = useState(new Set());
  const [closedCtViews, setClosedCtViews] = useState(new Set());
  const [closedMrViews, setClosedMrViews] = useState(new Set());
  const [closedPainViews, setClosedPainViews] = useState(new Set());

  // State to manage the loading state
  const [loadingStates, setLoadingStates] = useState({
    [SPINESIM_MODALITIES.DX]: false,
    [SPINESIM_MODALITIES.CT]: false,
    [SPINESIM_MODALITIES.MR]: false,
    [SPINESIM_MODALITIES.POSTURE]: false,
    [SPINESIM_MODALITIES.PAIN]: false,
  });

  // State to manage the active content in tewin view
  const [activeMainTwinViewIndex, setActiveMainTwinViewIndex] = useState(0);
  const [activeMainPostureIndex, setActiveMainPostureIndex] = useState(0);
  const [activeComparedTwinViewIndex, setActiveComparedTwinViewIndex] =
    useState(0);
  const [activeComparedPostureIndex, setActiveComparedPostureIndex] =
    useState(0);
  const [closingViews, setClosingViews] = useState(new Set());

  const { getMedicalImagePath } = useFileStorage();

  const {
    digitalTwinCache,
    isConnected: isRemoteRenderConnected,
    isLoading: isRemoteRenderLoading,
  } = useRemoteRenderContext();
  const previousTwinIdRef = useRef(null);

  const { navigateToTwinEdit, params } = useRouteState();

  const handleEditClick = useCallback(() => {
    if (selectedTwin?.id && params.patientId) {
      navigateToTwinEdit(params.patientId, selectedTwin.id);
    } else {
    }
  }, [selectedTwin, params.patientId, navigateToTwinEdit]);

  const handleDeleteClick = useCallback(() => {
    if (!selectedTwin) return;

    showPopup(
      <DeleteDigitalTwinModal
        digitalTwin={selectedTwin}
        onDigitalTwinDeleted={onDeleteTwin}
      />
    );
  }, [selectedTwin, showPopup, onDeleteTwin]);

  const allPosturalImages = useMemo(
    () => [...posturalImages, ...posturalImagesNoBg],
    [posturalImages, posturalImagesNoBg]
  );

  const groupedThumbnailsStandard = useMemo(
    () =>
      getGroupedThumbnails(
        selectedTwin,
        instancesByModality,
        allPosturalImages,
        normalizedPainAssessments
      ),
    [
      selectedTwin,
      instancesByModality,
      allPosturalImages,
      normalizedPainAssessments,
    ]
  );

  const groupedThumbnailsStandardCompared = useMemo(() => {
    if (!comparedTwin) return [];
    return getGroupedThumbnails(
      comparedTwin,
      instancesByModality,
      allPosturalImages,
      normalizedPainAssessments
    );
  }, [
    normalizedPainAssessments,
    comparedTwin,
    instancesByModality,
    allPosturalImages,
  ]);

  const postureEntrySelectedTwin = useMemo(
    () =>
      groupedThumbnailsStandard.find(
        t => t.modality === SPINESIM_MODALITIES.POSTURE
      ),
    [groupedThumbnailsStandard]
  );

  const postureEntryComparedTwin = useMemo(
    () =>
      groupedThumbnailsStandardCompared.find(
        t => t.modality === SPINESIM_MODALITIES.POSTURE
      ),
    [groupedThumbnailsStandardCompared]
  );

  const orderedPostureInstancesSelectedTwin = useMemo(() => {
    const postureInstances = postureEntrySelectedTwin?.instances ?? [];
    return MORPHOLOGY_ORIENTATION_ORDER.map(o =>
      postureInstances.find(i => i.orientation === o)
    ).filter(Boolean);
  }, [postureEntrySelectedTwin]);

  const orderedPostureInstancesComparedTwin = useMemo(() => {
    const postureInstances = postureEntryComparedTwin?.instances ?? [];
    return MORPHOLOGY_ORIENTATION_ORDER.map(o =>
      postureInstances.find(i => i.orientation === o)
    ).filter(Boolean);
  }, [postureEntryComparedTwin]);

  const postureImagesSelectedTwin = useMemo(
    () => orderedPostureInstancesSelectedTwin.map(img => img.url),
    [orderedPostureInstancesSelectedTwin]
  );

  const postureImagesComparedTwin = useMemo(
    () => orderedPostureInstancesComparedTwin.map(img => img.url),
    [orderedPostureInstancesComparedTwin]
  );

  const frontPostureIndexSelectedTwin = useMemo(
    () =>
      orderedPostureInstancesSelectedTwin.findIndex(
        i => i.orientation === MorphologyOrientation.FRONT
      ),
    [orderedPostureInstancesSelectedTwin]
  );

  const frontPostureIndexComparedTwin = useMemo(
    () =>
      orderedPostureInstancesComparedTwin.findIndex(
        i => i.orientation === MorphologyOrientation.FRONT
      ),
    [orderedPostureInstancesComparedTwin]
  );

  const loadMedicalImageView = useCallback(
    async (instance, forceReload = false) => {
      try {
        if (!renderClient || !renderClient.isConnected) {
          return null;
        }

        if (!instance) {
          return null;
        }

        const cacheKey = generateCacheKey(instance);

        // Check if already cached and not forcing reload
        if (!forceReload && digitalTwinCache.hasView(cacheKey)) {
          return cacheKey;
        }

        const path = await getMedicalImagePath(
          instance.tenant_id,
          instance.patient_id,
          instance._id,
          instance.file_name
        );

        const viewType = convertKey(instance.modality, instance.orientation);

        const viewConfig = {
          path: path,
          view: viewType,
        };

        const response = await renderClient.loadSpinesimVisualizationView(
          JSON.stringify(viewConfig)
        );

        let viewData;
        if (typeof response === "string") {
          try {
            viewData = JSON.parse(response);
          } catch (e) {
            console.error("[DigitalTwin] Failed to parse response:", e);
            viewData = { viewId: "error", viewName: "error" };
          }
        } else {
          viewData = response;
        }

        if (Array.isArray(viewData)) {
          viewData.forEach(view => {
            view.cacheKey = cacheKey;
            view.instance = instance;
          });
        } else {
          viewData.cacheKey = cacheKey;
          viewData.instance = instance;
        }

        digitalTwinCache.setView(cacheKey, viewData);
        return cacheKey;
      } catch (error) {
        return null;
      }
    },
    [renderClient, digitalTwinCache, getMedicalImagePath]
  );

  const [displayType, setDisplayType] = useState(DigitalTwinDisplayTypes.TWIN);

  const setModalityLoading = useCallback((modality, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [modality]: isLoading,
    }));
  }, []);

  //region Callbacks
  const handleSelectModality = useCallback(
    (_index, modality, isComparedTwin = false) => {
      // Helper function to handle the common modality selection logic
      const handleModalitySelection = (
        modality,
        thumbnails,
        setModalityCallback,
        resetCallback
      ) => {
        setModalityCallback(modality);
        // Reset relevant closed views set
        if (modality === SPINESIM_MODALITIES.DX) {
          setClosedDxViews(new Set());
        } else if (modality === SPINESIM_MODALITIES.CT) {
          setClosedCtViews(new Set());
        } else if (modality === SPINESIM_MODALITIES.MR) {
          setClosedMrViews(new Set());
        }

        if (modality === SPINESIM_MODALITIES.DX) {
          setClosedDxViews(new Set());
        } else if (modality === SPINESIM_MODALITIES.CT) {
          setClosedCtViews(new Set());
        } else if (modality === SPINESIM_MODALITIES.MR) {
          setClosedMrViews(new Set());
        } else if (modality === SPINESIM_MODALITIES.PAIN) {
          setClosedPainViews(new Set());
        }

        const modalityData = thumbnails.find(
          item => item.modality === modality
        );

        if (modalityData) {
          const instances = modalityData.instances;
          setCurrentModalityInstances(instances);
          if (!isComparedTwin) {
            setVisibleInstances(instances);
          }

          if (modality === SPINESIM_MODALITIES.POSTURE) {
            // Find the index of the front image if it exists
            const frontImageIndex = instances.findIndex(
              inst => inst.orientation === MorphologyOrientation.FRONT
            );
            setActiveMainPostureIndex(
              frontImageIndex >= 0 ? frontImageIndex : 0
            );
          } else {
            // Reset to first image for other modalities
            resetCallback(0);
          }
        } else {
          if (!isComparedTwin) {
            setCurrentModalityInstances([]);
            setVisibleInstances([]);
          }
        }
      };

      // Choose which twin to update based on isComparedTwin flag
      if (isComparedTwin) {
        handleModalitySelection(
          modality,
          groupedThumbnailsStandardCompared,
          setSelectedComparedTwinModality,
          setActiveComparedTwinViewIndex
        );
      } else {
        handleModalitySelection(
          modality,
          groupedThumbnailsStandard,
          setSelectedMainTwinModality,
          setActiveMainTwinViewIndex
        );
      }
    },
    [
      groupedThumbnailsStandard,
      groupedThumbnailsStandardCompared,
      setClosedPainViews,
      setClosedDxViews,
      setClosedCtViews,
      setClosedMrViews,
      setCurrentModalityInstances,
      setVisibleInstances,
      setActiveMainPostureIndex,
      setActiveMainTwinViewIndex,
    ]
  );

  // Function for selecting main twin modality
  const handleSelectMainTwinModality = useCallback(
    (_index, modality) => {
      handleSelectModality(_index, modality, false);
    },
    [handleSelectModality]
  );

  // Function for selecting compared twin modality
  const handleSelectComparedTwinModality = useCallback(
    (_index, modality) => {
      handleSelectModality(_index, modality, true);
    },
    [handleSelectModality]
  );

  const handleImageClose = (instanceId, viewId, index, viewType) => {
    const viewKey = viewId || instanceId || `index-${index}-${viewType}`;

    setClosingViews(prev => {
      const newSet = new Set(prev);
      newSet.add(viewKey);
      return newSet;
    });

    setTimeout(() => {
      if (viewType === SPINESIM_MODALITIES.POSTURE) {
        setVisibleInstances(prevInstances =>
          prevInstances.filter((instance, idx) => {
            if (instanceId && instance._id) {
              return instance._id !== instanceId;
            }
            return idx !== index;
          })
        );
      } else if (viewType === SPINESIM_MODALITIES.DX) {
        setClosedDxViews(prev => {
          const newSet = new Set(prev);
          newSet.add(viewId || instanceId);
          return newSet;
        });
      } else if (viewType === SPINESIM_MODALITIES.CT) {
        setClosedCtViews(prev => {
          const newSet = new Set(prev);
          newSet.add(viewId || instanceId);
          return newSet;
        });
      } else if (viewType === SPINESIM_MODALITIES.MR) {
        setClosedMrViews(prev => {
          const newSet = new Set(prev);
          newSet.add(viewId || instanceId);
          return newSet;
        });
      } else if (viewType === SPINESIM_MODALITIES.PAIN) {
        setClosedPainViews(prev => {
          const newSet = new Set(prev);
          newSet.add(viewId || instanceId);
          return newSet;
        });
      }

      setClosingViews(prev => {
        const newSet = new Set(prev);
        newSet.delete(viewKey);
        return newSet;
      });
    }, 500); // This should match animation duration in CSS
  };
  //endregion

  //region Setup

  useEffect(() => {
    if (postureImagesSelectedTwin.length > 0) {
      setActiveMainPostureIndex(
        frontPostureIndexSelectedTwin >= 0 ? frontPostureIndexSelectedTwin : 0
      );
    }
  }, [postureImagesSelectedTwin, frontPostureIndexSelectedTwin]);

  useEffect(() => {
    if (postureImagesComparedTwin.length > 0) {
      setActiveMainPostureIndex(
        frontPostureIndexComparedTwin >= 0 ? frontPostureIndexComparedTwin : 0
      );
    }
  }, [postureImagesComparedTwin, frontPostureIndexComparedTwin]);

  // Pre-fetch views for selected twin
  useEffect(() => {
    const preFetchViews = async twin => {
      if (!twin || !renderClient || !renderClient.isConnected) return;

      const {
        sagittal_dx_instance_id,
        coronal_dx_instance_id,
        ct_instance_id,
        mri_instance_id,
      } = twin;

      const { DX = [], CT = [], MR = [] } = instancesByModality;

      // Collect all instances to load and their modalities
      const instancesToLoad = [];

      if (sagittal_dx_instance_id) {
        const sagittalInst = DX.find(i => i._id === sagittal_dx_instance_id);
        if (sagittalInst) {
          instancesToLoad.push({
            instance: sagittalInst,
            modality: SPINESIM_MODALITIES.DX,
          });
        }
      }

      if (coronal_dx_instance_id) {
        const coronalInst = DX.find(i => i._id === coronal_dx_instance_id);
        if (coronalInst) {
          instancesToLoad.push({
            instance: coronalInst,
            modality: SPINESIM_MODALITIES.DX,
          });
        }
      }

      if (ct_instance_id) {
        const ctInst = CT.find(i => i._id === ct_instance_id);
        if (ctInst) {
          instancesToLoad.push({
            instance: ctInst,
            modality: SPINESIM_MODALITIES.CT,
          });
        }
      }

      if (mri_instance_id) {
        const mrInst = MR.find(i => i._id === mri_instance_id);
        if (mrInst) {
          instancesToLoad.push({
            instance: mrInst,
            modality: SPINESIM_MODALITIES.MR,
          });
        }
      }

      // Group instances by modality to set loading states
      const modalitiesLoading = new Set();
      instancesToLoad.forEach(({ modality }) => {
        if (!modalitiesLoading.has(modality)) {
          modalitiesLoading.add(modality);
          setModalityLoading(modality, true);
        }
      });

      try {
        // Load all instances
        const loadPromises = instancesToLoad.map(({ instance }) =>
          loadMedicalImageView(instance)
        );

        await Promise.all(loadPromises);

        // Set all modalities to not loading
        modalitiesLoading.forEach(modality => {
          setModalityLoading(modality, false);
        });
      } catch (error) {
        // Reset all loading states on error
        modalitiesLoading.forEach(modality => {
          setModalityLoading(modality, false);
        });
      }
    };

    preFetchViews(selectedTwin);
    preFetchViews(comparedTwin);
  }, [
    selectedTwin,
    comparedTwin,
    instancesByModality,
    renderClient,
    loadMedicalImageView,
    setModalityLoading,
  ]);

  useEffect(() => {
    if (groupedThumbnailsStandard.length > 0 && !selectedMainTwinModality) {
      const preferredModality = getPreferredModality(groupedThumbnailsStandard);
      if (preferredModality) {
        handleSelectMainTwinModality(0, preferredModality);
      }
    }
  }, [
    groupedThumbnailsStandard,
    handleSelectMainTwinModality,
    selectedMainTwinModality,
  ]);
  //endregion

  //region Cleanup

  useEffect(() => {
    if (
      selectedTwin?.id &&
      selectedTwin.id !== previousTwinIdRef.current &&
      groupedThumbnailsStandard.length > 0
    ) {
      previousTwinIdRef.current = selectedTwin.id;
      // Refresh modality - prioritize DX, then first available
      const preferredModality = getPreferredModality(groupedThumbnailsStandard);
      if (preferredModality) {
        setSelectedMainTwinModality(preferredModality);
        handleSelectMainTwinModality(0, preferredModality);
      }
    }
  }, [
    selectedTwin?.id,
    groupedThumbnailsStandard,
    handleSelectMainTwinModality,
  ]);

  // Set modality for compared twin when it's mounted or changed
  useEffect(() => {
    if (comparedTwin && groupedThumbnailsStandardCompared.length > 0) {
      // Only set if not already set or if compared twin changed
      if (!selectedComparedTwinModality) {
        const preferredModality = getPreferredModality(
          groupedThumbnailsStandardCompared
        );
        if (preferredModality) {
          handleSelectComparedTwinModality(0, preferredModality);
        }
      }
    }
  }, [
    comparedTwin,
    groupedThumbnailsStandardCompared,
    selectedComparedTwinModality,
    handleSelectComparedTwinModality,
  ]);

  //region Rendering

  const MemoizedPatientInformation = useMemo(
    () => (
      <PatientInformation
        conditionsData={conditionsData}
        qualityOfLifeIndicators={qualityOfLifeIndicators}
        weightValue={selectedWeightValue}
        heightValue={selectedHeightValue}
      />
    ),
    [
      selectedTwin?.id,
      clinicalHistory,
      conditionsData,
      qualityOfLifeIndicators,
      selectedWeightValue,
      selectedHeightValue,
    ]
  );
  const MemoizedComparedPatientInformation = useMemo(
    () =>
      comparedTwin ? (
        <PatientInformation
          conditionsData={conditionsDataCompared}
          qualityOfLifeIndicators={qualityOfLifeIndicatorsCompared}
          weightValue={comparedWeightValue}
          heightValue={comparedHeightValue}
        />
      ) : null,
    [
      comparedTwin?.id,
      comparedClinicalHistory,
      conditionsDataCompared,
      qualityOfLifeIndicatorsCompared,
      comparedWeightValue,
      comparedHeightValue,
    ]
  );
  const MemoizedSpineMeasurements = useMemo(
    () => (
      <SpineMeasurements
        selectedTwin={selectedTwin}
        instancesByModality={instancesByModality}
      />
    ),
    [selectedTwin?.id, instancesByModality]
  );

  const renderPostureGrid = (views, handleImageClose) => {
    // Sort views according to POSTURE_ORDER

    return (
      <div className="DigitalTwinVisualization-posture-grid">
        {views.map((instance, index) => (
          <ImageViewer
            key={`image-container-${instance._id || index}`}
            className={
              isViewClosing(
                null,
                instance._id,
                index,
                SPINESIM_MODALITIES.POSTURE,
                closingViews
              )
                ? "DigitalTwinVisualization-closing"
                : ""
            }
            instance={instance}
            onContentLoad={() => {}}
            onCameraClick={() => {}}
            step="digitalTwin"
            viewType={SPINESIM_MODALITIES.POSTURE}
          >
            <img src={instance.url} alt={`Instance ${index}`} />
          </ImageViewer>
        ))}
      </div>
    );
  };

  const renderPainAssessmentGrid = painAssessment => {
    if (!painAssessment) {
      return (
        <div className="no-view-message">No pain assessment data available</div>
      );
    }

    const orientationNames = ["Anterior", "Left", "Posterior", "Right"];

    // Filter out closed orientations
    const visibleOrientations = orientationNames.filter(
      (orientationName, orientation) => {
        const viewKey = `pain-${orientation}`;
        return !closedPainViews.has(viewKey);
      }
    );

    // If all orientations are closed, show message
    if (visibleOrientations.length === 0) {
      return (
        <div className="no-view-message">
          No pain assessment views available
        </div>
      );
    }

    return (
      <div className="DigitalTwinVisualization-pain-grid">
        {orientationNames.map((orientationName, orientation) => {
          const viewKey = `pain-${orientation}`;

          // Skip if this orientation is closed
          if (closedPainViews.has(viewKey)) {
            return null;
          }

          return (
            <ImageViewer
              key={`pain-orientation-${orientation}`}
              className={
                isViewClosing(
                  null,
                  viewKey,
                  orientation,
                  SPINESIM_MODALITIES.PAIN,
                  closingViews
                )
                  ? "DigitalTwinVisualization-closing"
                  : ""
              }
              onContentLoad={() => {}}
              onClose={() =>
                handleImageClose(
                  viewKey,
                  null,
                  orientation,
                  SPINESIM_MODALITIES.PAIN
                )
              }
              onCameraClick={() => {}}
            >
              <PainAssessmentBodyDiagram
                assessment={painAssessment}
                orientation={orientation}
                orientationName={orientationName}
              />
            </ImageViewer>
          );
        })}
      </div>
    );
  };

  // Render DX grid (X-rays)
  const renderDxGrid = (views, handleImageClose, renderClient) => {
    return (
      <div className="DigitalTwinVisualization-dx-grid">
        {views.map(view => {
          return (
            <ImageViewer
              key={`image-container-${view.key}`}
              className={
                isViewClosing(
                  view.viewData.viewId,
                  view.instance._id,
                  null,
                  SPINESIM_MODALITIES.DX,
                  closingViews
                )
                  ? "DigitalTwinVisualization-closing"
                  : ""
              }
              viewData={view.viewData}
              instance={view.instance}
              onClose={() =>
                handleImageClose(
                  view.instance._id,
                  view.viewData.viewId,
                  null,
                  SPINESIM_MODALITIES.DX
                )
              }
              onContentLoad={() => {}}
              onCameraClick={() => {}}
              onBrightnessClick={() => {}}
              step="digitalTwin"
              viewType={view.viewName}
              remoteRenderClient={renderClient}
              viewId={view.viewData.viewId}
              planId={null}
              instanceId={view.instance?._id}
              displaySettingsViewType={getDisplaySettingsViewType(
                view.viewName
              )}
              persistedDisplaySettings={
                view.instance?.display_settings?.[
                  getDisplaySettingsViewType(view.viewName)
                ]
              }
              onDisplaySettingsUpdate={updateLocalDisplaySettings}
            >
              <RemoteViewer
                remoteRenderClient={renderClient}
                viewData={view.viewData}
                sliderWidth="80%"
              />
            </ImageViewer>
          );
        })}
      </div>
    );
  };

  // Update single view grid renderer
  const renderSingleViewGrid = (
    view,
    modality,
    handleImageClose,
    renderClient
  ) => {
    return (
      <div
        className={`DigitalTwinVisualization-${modality.toLowerCase()}-grid single-view`}
      >
        <ImageViewer
          key={`image-container-${view.key}`}
          className={
            isViewClosing(
              view.viewData.viewId,
              view.instance._id,
              0,
              modality,
              closingViews
            )
              ? "DigitalTwinVisualization-closing"
              : ""
          }
          viewData={view.viewData}
          instance={view.instance}
          onContentLoad={() => {}}
          onCameraClick={() => {}}
          onBrightnessClick={() => {}}
          step="digitalTwin"
          viewType={view.viewName}
          remoteRenderClient={renderClient}
          viewId={view.viewData.viewId}
          planId={null}
          instanceId={view.instance?._id}
          displaySettingsViewType={getDisplaySettingsViewType(view.viewName)}
          persistedDisplaySettings={
            view.instance?.display_settings?.[
              getDisplaySettingsViewType(view.viewName)
            ]
          }
          onDisplaySettingsUpdate={updateLocalDisplaySettings}
        >
          <RemoteViewer
            remoteRenderClient={renderClient}
            viewData={view.viewData}
            sliderWidth="80%"
          />
        </ImageViewer>
      </div>
    );
  };

  // Update two-column grid renderer
  const renderTwoColumnGrid = (
    views,
    modality,
    handleImageClose,
    renderClient
  ) => {
    return (
      <div
        className={`DigitalTwinVisualization-${modality.toLowerCase()}-grid two-column`}
      >
        {views.map((view, index) => {
          return (
            <div key={`column-${index}`} className={`column-${index}`}>
              <ImageViewer
                key={`image-container-${view.key}`}
                className={
                  isViewClosing(
                    view.viewData.viewId,
                    view.instance._id,
                    view.index,
                    modality,
                    closingViews
                  )
                    ? "DigitalTwinVisualization-closing"
                    : ""
                }
                viewData={view.viewData}
                instance={view.instance}
                onContentLoad={() => {}}
                onCameraClick={() => {}}
                onBrightnessClick={() => {}}
                step="digitalTwin"
                viewType={view.viewName}
                remoteRenderClient={renderClient}
                viewId={view.viewData.viewId}
                planId={null}
                instanceId={view.instance?._id}
                displaySettingsViewType={getDisplaySettingsViewType(
                  view.viewName
                )}
                persistedDisplaySettings={
                  view.instance?.display_settings?.[
                    getDisplaySettingsViewType(view.viewName)
                  ]
                }
                onDisplaySettingsUpdate={updateLocalDisplaySettings}
              >
                <RemoteViewer
                  remoteRenderClient={renderClient}
                  viewData={view.viewData}
                  sliderWidth="80%"
                />
              </ImageViewer>
            </div>
          );
        })}
      </div>
    );
  };
  // Render CT/MR grid with multi-view layout
  const renderMultiViewGrid = (
    views,
    modality,
    handleImageClose,
    renderClient
  ) => {
    return (
      <div
        className={`DigitalTwinVisualization-${modality.toLowerCase()}-grid multi-view`}
      >
        {/* First column - first view (takes full height) */}
        <div className="first-column">
          <ImageViewer
            key={`image-container-${views[0].key}`}
            className={
              isViewClosing(
                views[0].viewData.viewId,
                views[0].instance._id,
                0,
                modality,
                closingViews
              )
                ? "DigitalTwinVisualization-closing"
                : ""
            }
            viewData={views[0].viewData}
            instance={views[0].instance}
            onContentLoad={() => {}}
            onClose={() =>
              handleImageClose(
                views[0].instance._id,
                views[0].viewData.viewId,
                0,
                modality
              )
            }
            onCameraClick={() => {}}
            onBrightnessClick={() => {}}
            step="digitalTwin"
            viewType={views[0].viewName}
            remoteRenderClient={renderClient}
            viewId={views[0].viewData.viewId}
            planId={null}
            instanceId={views[0].instance?._id}
            displaySettingsViewType={getDisplaySettingsViewType(
              views[0].viewName
            )}
            persistedDisplaySettings={
              views[0].instance?.display_settings?.[
                getDisplaySettingsViewType(views[0].viewName)
              ]
            }
            onDisplaySettingsUpdate={updateLocalDisplaySettings}
          >
            <RemoteViewer
              remoteRenderClient={renderClient}
              viewData={views[0].viewData}
              sliderWidth="80%"
            />
          </ImageViewer>
        </div>

        {/* Second column - contains remaining views stacked */}
        <div className="second-column">
          {views.slice(1).map(view => {
            return (
              <ImageViewer
                key={`image-container-${view.key}`}
                className={
                  isViewClosing(
                    view.viewData.viewId,
                    view.instance._id,
                    view.index,
                    modality,
                    closingViews
                  )
                    ? "DigitalTwinVisualization-closing"
                    : ""
                }
                viewData={view.viewData}
                instance={view.instance}
                onContentLoad={() => {}}
                onCameraClick={() => {}}
                onBrightnessClick={() => {}}
                step="digitalTwin"
                viewType={view.viewName}
                remoteRenderClient={renderClient}
                viewId={view.viewData.viewId}
                planId={null}
                instanceId={view.instance?._id}
                displaySettingsViewType={getDisplaySettingsViewType(
                  view.viewName
                )}
                persistedDisplaySettings={
                  view.instance?.display_settings?.[
                    getDisplaySettingsViewType(view.viewName)
                  ]
                }
                onDisplaySettingsUpdate={updateLocalDisplaySettings}
              >
                <RemoteViewer
                  remoteRenderClient={renderClient}
                  viewData={view.viewData}
                  sliderWidth="80%"
                />
              </ImageViewer>
            );
          })}
        </div>
      </div>
    );
  };

  // Render CT or MR grid based on number of views
  const renderVolumeModalityGrid = (
    views,
    modality,
    handleImageClose,
    renderClient
  ) => {
    if (views.length === 1) {
      return renderSingleViewGrid(
        views[0],
        modality,
        handleImageClose,
        renderClient
      );
    } else if (views.length === 2) {
      return renderTwoColumnGrid(
        views,
        modality,
        handleImageClose,
        renderClient
      );
    } else {
      return renderMultiViewGrid(
        views,
        modality,
        handleImageClose,
        renderClient
      );
    }
  };

  // Main function to render the appropriate grid based on modality

  const renderModalityGrid = (
    modality,
    views,
    closedViews,
    handleImageClose,
    renderClient
  ) => {
    const remoteMedicalViewState = getRemoteMedicalViewState({
      modality,
      views,
      isLocalLoading: loadingStates[modality] || false,
      isRemoteRenderConnected,
      isRemoteRenderLoading,
    });
    const remoteMedicalLoadingMessage = remoteMedicalViewState.isConnecting
      ? t("474")
      : t("475");

    // Handle pain assessment modality separately
    if (modality === SPINESIM_MODALITIES.PAIN) {
      const painAssessment = groupedThumbnailsStandard.find(
        t => t.modality === SPINESIM_MODALITIES.PAIN
      )?.instances?.[0];

      return renderPainAssessmentGrid(painAssessment);
    }

    // Show loading if no views AND modality is loading
    if (
      views.length === 0 &&
      remoteMedicalViewState.isLoading
    ) {
      return (
        <ViewLoader
          isLoading={true}
          message={remoteMedicalLoadingMessage}
        />
      );
    }

    // Show empty state if no views and not loading
    if (views.length === 0) {
      return (
        <div className="no-view-message">No {modality} views available</div>
      );
    }

    // Filter and sort views
    const filteredViews = prepareFilteredViews(views, closedViews, modality);

    // If all views are closed after filtering, show message
    if (filteredViews.length === 0) {
      return (
        <div className="no-view-message">No {modality} views available</div>
      );
    }

    // Use appropriate renderer based on modality
    switch (modality) {
      case SPINESIM_MODALITIES.PAIN:
        const painAssessment = groupedThumbnailsStandard.find(
          t => t.modality === SPINESIM_MODALITIES.PAIN
        )?.instances?.[0];
        return renderPainAssessmentTwinContent(painAssessment);
      case SPINESIM_MODALITIES.POSTURE:
        return renderPostureGrid(filteredViews, handleImageClose);

      case SPINESIM_MODALITIES.DX:
        return renderDxGrid(filteredViews, handleImageClose, renderClient);

      case SPINESIM_MODALITIES.CT:
      case SPINESIM_MODALITIES.MR:
        return renderVolumeModalityGrid(
          filteredViews,
          modality,
          handleImageClose,
          renderClient
        );

      default:
        return null;
    }
  };

  // Render posture twin content using ImageCarousel
  const renderPostureTwinContent = (
    postureImages,
    carouselIndex,
    carouselCallback
  ) => {
    if (postureImages.length === 0) {
      return <div className="no-view-message">No posture images available</div>;
    }
    return (
      <div
        className={`DigitalTwinVisualization-twin-image ${
          comparisonMode ? "compare" : ""
        }`}
      >
        <ImageCarousel
          images={postureImages}
          activeIndex={carouselIndex}
          onImageClick={carouselCallback}
        />
      </div>
    );
  };

  const renderPainAssessmentTwinContent = assessment => {
    if (!assessment) {
      return (
        <div className="no-view-message">No pain assessment data available</div>
      );
    }

    return (
      <div
        className={`DigitalTwinVisualization-twin-image ${
          comparisonMode ? "compare" : ""
        }`}
      >
        <div className="pain-assessment-container">
          <PainAssessmentViewer assessment={assessment} />
        </div>
      </div>
    );
  };

  // Render medical view carousel for DX, CT, and MR
  const renderMedicalViewCarousel = (
    views,
    modality,
    carouselIndex,
    carouselCallback
  ) => {
    // Filter views for carousel - for CT/MR exclude axial views
    const carouselViews = prepareCarouselViews(views, modality);

    if (carouselViews.length === 0) {
      return (
        <div className="no-view-message">No {modality} views available</div>
      );
    }

    const viewsKey = carouselViews
      .map(v => v.key || v.viewData?.viewId)
      .join("-");

    return (
      <div
        className={`DigitalTwinVisualization-twin-image ${
          comparisonMode ? "compare" : ""
        }`}
      >
        <ViewCarousel
          key={`view-carousel-${modality}-${viewsKey}`}
          views={carouselViews}
          activeIndex={carouselIndex}
          onViewClick={carouselCallback}
          renderClient={renderClient}
        />
      </div>
    );
  };

  const renderDefaultTwinContent = () => {
    const remoteMedicalViewState = getRemoteMedicalViewState({
      modality: selectedMainTwinModality,
      views: [],
      isLocalLoading: Object.values(loadingStates).some(loading => loading),
      isRemoteRenderConnected,
      isRemoteRenderLoading,
    });
    const remoteMedicalLoadingMessage = remoteMedicalViewState.isConnecting
      ? t("474")
      : t("475");

    return (
      <>
        <div className="DigitalTwinVisualization-twin-image">
          <ViewLoader
            isLoading={remoteMedicalViewState.isLoading}
            message={remoteMedicalLoadingMessage}
          >
            {!remoteMedicalViewState.isLoading && (
              <div className="no-view-message"></div>
            )}
          </ViewLoader>
        </div>
      </>
    );
  };

  // Main rendering functions
  const renderGrid = () => {
    const views = getViewsForModality(
      selectedMainTwinModality,
      selectedTwin,
      instancesByModality,
      digitalTwinCache,
      generateCacheKey,
      visibleInstances
    );

    let closedViews;
    switch (selectedMainTwinModality) {
      case SPINESIM_MODALITIES.DX:
        closedViews = closedDxViews;
        break;
      case SPINESIM_MODALITIES.CT:
        closedViews = closedCtViews;
        break;
      case SPINESIM_MODALITIES.MR:
        closedViews = closedMrViews;
        break;
      case SPINESIM_MODALITIES.PAIN:
        closedViews = closedPainViews;
        break;
      default:
        closedViews = new Set();
    }

    return renderModalityGrid(
      selectedMainTwinModality,
      views,
      closedViews,
      handleImageClose,
      renderClient
    );
  };

  const renderTwin = () => {
    const viewsSelectedTwin = getViewsForModality(
      selectedMainTwinModality,
      selectedTwin,
      instancesByModality,
      digitalTwinCache,
      generateCacheKey,
      visibleInstances
    );

    const viewsComparedTwin = getViewsForModality(
      selectedComparedTwinModality,
      comparedTwin,
      instancesByModality,
      digitalTwinCache,
      generateCacheKey,
      visibleInstances
    );

    const painAssessmentSelectedTwin = groupedThumbnailsStandard.find(
      t => t.modality === SPINESIM_MODALITIES.PAIN
    )?.instances?.[0];

    const painAssessmentComparedTwin = comparisonMode
      ? groupedThumbnailsStandardCompared.find(
          t => t.modality === SPINESIM_MODALITIES.PAIN
        )?.instances?.[0]
      : null;

    const Twin = props => {
      const {
        name,
        modality,
        posturalImages,
        views = [],
        painAssessment,
        posturalCarouselIndex,
        posturalCarouselCallback,
        medicalCarouselIndex,
        medicalCarouselCallback,
        isCompared = false,
      } = props;

      const patientInformationContent = isCompared
        ? MemoizedComparedPatientInformation
        : MemoizedPatientInformation;

      const patientInfoColumn = (
        <div className="DigitalTwinVisualization-twin-info">
          {modality && patientInformationContent}
        </div>
      );

      const spineMeasurementsColumn = (
        <div className="DigitalTwinVisualization-spine-measurements">
          {modality && MemoizedSpineMeasurements}
        </div>
      );

      const imageColumn = (
        <div className="DigitalTwinVisualization-twin-content">
          {modality === SPINESIM_MODALITIES.PAIN
            ? renderPainAssessmentTwinContent(painAssessment)
            : modality === SPINESIM_MODALITIES.POSTURE
              ? renderPostureTwinContent(
                  posturalImages,
                  posturalCarouselIndex,
                  posturalCarouselCallback
                )
              : views.length > 0
                ? renderMedicalViewCarousel(
                    views,
                    modality,
                    medicalCarouselIndex,
                    medicalCarouselCallback
                  )
                : renderDefaultTwinContent()}
          {comparisonMode && (
            <div className="comparison-twin-label App-text-large">{name}</div>
          )}
        </div>
      );

      // In comparison mode: combine info columns
      if (comparisonMode) {
        const combinedInfoColumn = (
          <div className="DigitalTwinVisualization-twin-info-column">
            {patientInfoColumn}
            {spineMeasurementsColumn}
          </div>
        );

        return (
          <div
            className={`DigitalTwinVisualization-twin ${
              isCompared ? "compared" : ""
            }`}
          >
            {isCompared ? (
              <>
                {imageColumn}
                {combinedInfoColumn}
              </>
            ) : (
              <>
                {combinedInfoColumn}
                {imageColumn}
              </>
            )}
          </div>
        );
      }

      // Not in comparison mode: 3-column layout
      return (
        <div className="DigitalTwinVisualization-twin three-column">
          {patientInfoColumn}
          {imageColumn}
          {spineMeasurementsColumn}
        </div>
      );
    };

    return (
      <div className="DigitalTwinVisualization-twin-container">
        <Twin
          name={selectedTwin?.name}
          modality={selectedMainTwinModality}
          posturalImages={postureImagesSelectedTwin}
          views={viewsSelectedTwin}
          painAssessment={painAssessmentSelectedTwin}
          posturalCarouselIndex={activeMainPostureIndex}
          posturalCarouselCallback={setActiveMainPostureIndex}
          medicalCarouselIndex={activeMainTwinViewIndex}
          medicalCarouselCallback={setActiveMainTwinViewIndex}
        ></Twin>
        {comparisonMode && (
          <Twin
            name={comparedTwin?.name}
            modality={selectedComparedTwinModality}
            posturalImages={postureImagesComparedTwin}
            views={viewsComparedTwin}
            painAssessment={painAssessmentComparedTwin}
            posturalCarouselIndex={activeComparedPostureIndex}
            posturalCarouselCallback={setActiveComparedPostureIndex}
            medicalCarouselIndex={activeComparedTwinViewIndex}
            medicalCarouselCallback={setActiveComparedTwinViewIndex}
            isCompared={true}
          ></Twin>
        )}
      </div>
    );
  };

  // Helper function to format the header title
  const formatHeaderTitle = () => {
    if (comparisonMode) {
      const selectedTwinName = selectedTwin?.name || "Unknown Twin";
      const comparedTwinName = comparedTwin?.name || "Unknown Twin";
      return `Digital Twins: ${selectedTwinName} vs ${comparedTwinName}`;
    } else {
      const twinName = selectedTwin?.name || "Unknown Twin";
      return `Digital Twin: ${twinName}`;
    }
  };

  if (!selectedTwin) {
    return;
  }

  return (
    <div className="DigitalTwinVisualization-container">
      <div className="DigitalTwinVisualization-header">
        <div className="DigitalTwinVisualization-title App-title-widgets">
          <span>{formatHeaderTitle()}</span>
          <IconButton
            icon={EditIcon}
            iconSize={IconSize.DEFAULT}
            noBgColor
            onClick={handleEditClick}
            title="Edit Digital Twin"
          />
          {!comparisonMode && canDelete && (
            <IconButton
              icon={DeleteIcon}
              iconSize={IconSize.DEFAULT}
              noBgColor
              onClick={handleDeleteClick}
              title="Delete Digital Twin"
            />
          )}
        </div>

        <div className="DigitalTwinVisualization-header-right">
          {formatTwinCreationDate(
            selectedTwin,
            comparisonMode ? comparedTwin : null,
            {
              locale: dateSettings?.locale,
              format: dateSettings?.format,
            }
          ) && (
            <span className="DigitalTwinVisualization-date">
              {formatTwinCreationDate(
                selectedTwin,
                comparisonMode ? comparedTwin : null,
                {
                  locale: dateSettings?.locale,
                  format: dateSettings?.format,
                }
              )}
            </span>
          )}

          <ToggleButton
            icon={CompareIcon}
            iconSize={IconSize.DEFAULT}
            isSelected={comparisonModeEnabled}
            onClick={onToggleComparisonMode}
            text={t("444")}
          />
        </div>
      </div>
      <div
        className={`DigitalTwinVisualization-content ${
          comparisonMode ? "compare" : ""
        }`}
      >
        <div className="DigitalTwinVisualization-twin-tab">
          <ThumbnailTab
            thumbnails={groupedThumbnailsStandard}
            selectedThumbnail={selectedMainTwinModality}
            onSelectThumbnail={handleSelectMainTwinModality}
            painAssessments={normalizedPainAssessments}
          />
          {!comparisonMode && (
            <div className="DigitalTwinVisualization-display-toggle">
              <IconButton
                icon={
                  displayType === DigitalTwinDisplayTypes.TWIN
                    ? GridIcon
                    : TwinIcon
                }
                iconSize={IconSize.DEFAULT}
                noBgColor
                onClick={() =>
                  setDisplayType(
                    displayType === DigitalTwinDisplayTypes.TWIN
                      ? DigitalTwinDisplayTypes.GRID
                      : DigitalTwinDisplayTypes.TWIN
                  )
                }
                title={
                  displayType === DigitalTwinDisplayTypes.TWIN
                    ? t("439")
                    : t("440")
                }
              />
            </div>
          )}
        </div>
        <div className="DigitalTwinVisualization-content-container">
          {displayType === DigitalTwinDisplayTypes.GRID
            ? renderGrid()
            : renderTwin()}
        </div>
        {comparisonMode && (
          <div
            className={`DigitalTwinVisualization-twin-tab ${
              comparisonMode ? "compare" : ""
            }`}
          >
            <ThumbnailTab
              thumbnails={groupedThumbnailsStandardCompared}
              selectedThumbnail={selectedComparedTwinModality}
              onSelectThumbnail={handleSelectComparedTwinModality}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalTwinVisualization;
